<script setup>
  import { computed, onMounted, reactive, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { BFormTextarea, BFormInput, BFormCheckboxGroup, BFormCheckbox, BFormRadio, BFormRadioGroup, BFormSelect } from 'bootstrap-vue-next';
  import IncludeExclude from '../ui/IncludeExclude.vue';
  import SuggestionBox from '../ui/SuggestionBox.vue';
  import CriteriaCard from '../ui/CriteriaCard.vue';

  const { t } = useI18n();

  const props = defineProps({
    languages: {
      type: Array,
      required: true
    },
    audiences: {
      type: Array,
      required: true
    },
    illustrated_books: {
      type: Array,
      required: true
    },
    lists: {
      type: Array,
      required: true
    },
    classification_scheme: {
      type: String,
      required: false,
      default: 'feedbooks'
    },
    // Whether to filter on the purchase history of the current library
    purchase_history: {
      type: Boolean,
      required: false,
      default: false
    },
    // Whether to filter on the offer model, as the market displays it
    ownership_model: {
      type: Boolean,
      required: false,
      default: false
    },
    // Options of the license duration, loan count and concurrent users filters
    license_terms: {
      type: Object,
      required: false,
      default: () => ({ lifetime: [], tokens: [], concurrency: [] })
    },
    // Purchase statuses: the main ones, then the ones narrowing the purchased publications
    purchased_statuses: {
      type: Object,
      required: false,
      default: () => ({ options: [], sub_options: [] })
    },
    // Accessibility filters, each one a condition the titles must all fulfill
    accessibilities: {
      type: Array,
      required: false,
      default: () => []
    },
    // Options of the award recognition filter
    award_positions: {
      type: Array,
      required: false,
      default: () => []
    },
    // Whether to filter on the publishers from Quebec, as the market or the network highlights them
    from_quebec_filter: {
      type: Boolean,
      required: false,
      default: false
    },
    // Criteria of the search the page was opened from
    initial: {
      type: Object,
      required: false,
      default: () => ({})
    },
    // Within another form, the name its search params are posted under (ContextOverridable#with_search_criteria)
    input_name: {
      type: String,
      required: false,
      default: null
    },
    // Search params the form opened with, when it is displayed again with the edited criteria of a failed submission
    initial_criteria: {
      type: String,
      required: false,
      default: null
    },
    // The criteria to show (AdvancedSearchHelper::EMBEDDED_CRITERIA), all of them when not given. The hidden ones keep
    // their value, so that they still apply
    // (ContextOverridable#with_search_criteria)
    shown_criteria: {
      type: Array,
      required: false,
      default: null
    }
  });

  // Values of a blank form, the ones the cards are compared to and cleared to
  const ANY = 'all';
  const DEFAULTS = {
    catalogs: ['traditional'],
    epub_options: ['reflowable', 'fixed_layout'],
    audiobook_options: ['unabridged', 'abridged'],
    accessibility_options: ['accessibility_fully']
  };
  const date_range_options = [
    { value: null, text: '' },
    { value: 'recent_week', text: t('advanced_search.date_range.recent_week') },
    { value: 'recent_month', text: t('advanced_search.date_range.recent_month') },
    { value: 'recent_year', text: t('advanced_search.date_range.recent_year') }
  ]

  const initial = props.initial;
  const initial_formats = initial.book_formats || ['epub', 'pdf', 'audiobook'];

  const query = ref(initial.query || '');
  const isbn = ref(initial.isbns || '');
  const title = ref(initial.title || '');
  const description = ref(initial.description || '');
  const include_imprints = ref(initial.include_imprints || []);
  const exclude_imprints = ref(initial.exclude_imprints || []);
  const include_contributors = ref(initial.include_contributors || []);
  const exclude_contributors = ref(initial.exclude_contributors || []);
  const include_series = ref(initial.include_series || []);
  const exclude_series = ref(initial.exclude_series || []);
  const include_awards = ref(initial.include_awards || []);
  const exclude_awards = ref(initial.exclude_awards || []);
  const include_collections = ref(initial.include_collections || []);
  const exclude_collections = ref(initial.exclude_collections || []);
  const include_languages = ref(initial.include_languages || []);
  const exclude_languages = ref(initial.exclude_languages || []);
  const include_audiences = ref(initial.include_audiences || []);
  const exclude_audiences = ref(initial.exclude_audiences || []);
  const include_categories = ref(initial.include_categories || []);
  const exclude_categories = ref(initial.exclude_categories || []);
  const include_illustrated_books = ref(initial.include_illustrated_books || []);
  const exclude_illustrated_books = ref(initial.exclude_illustrated_books || []);
  const publication_date_start = ref(initial.publication_date_start || '');
  const publication_date_end = ref(initial.publication_date_end || '');
  const release_date = ref(initial.release_date || '');
  const creation_date_start = ref(initial.creation_date_start || '');
  const creation_date_end = ref(initial.creation_date_end || '');
  const offer_create_date = ref(initial.offer_create_date || '');
  const catalogs = ref(initial.catalogs || [...DEFAULTS.catalogs]);
  const format_epub = ref(initial_formats.includes('epub'));
  const format_pdf = ref(initial_formats.includes('pdf'));
  const format_audiobook = ref(initial_formats.includes('audiobook'));
  const epub_options = ref(format_epub.value ? (initial.epub_options || [...DEFAULTS.epub_options]) : []);
  const audiobook_options = ref(format_audiobook.value ? (initial.audiobook_options || [...DEFAULTS.audiobook_options]) : []);
  const accessibility = ref(initial.accessibilities ? 'accessible' : ANY);
  const accessibility_options = ref(initial.accessibilities || [...DEFAULTS.accessibility_options]);
  const list = ref(initial.list || '');
  const purchased = ref(initial.purchased || ANY);
  const days_remaining = ref(initial.days_remaining || '');
  const loans_remaining = ref(initial.loans_remaining || '');
  const lifetime = ref(initial.lifetime || ANY);
  const tokens = ref(initial.tokens || ANY);
  const concurrency = ref(initial.concurrency || ANY);
  const license_term_options = Object.fromEntries(['lifetime', 'tokens', 'concurrency'].map((param) => (
    [param, [{ value: ANY, text: t(`advanced_search.${param}.all`) }, ...props.license_terms[param]]]
  )));
  const award_position = ref(initial.award_position || ANY);
  const award_position_options = [{ value: ANY, text: t('advanced_search.award_position.all') }, ...props.award_positions];
  const from_quebec = ref([true, false].includes(initial.from_quebec) ? String(initial.from_quebec) : ANY);
  const from_quebec_options = [
    { value: ANY, text: t('advanced_search.from_quebec.all') },
    { value: 'true', text: t('advanced_search.from_quebec.from_quebec') },
    { value: 'false', text: t('advanced_search.from_quebec.outside_quebec') }
  ];
  const ownership = ref([true, false].includes(initial.ownership) ? String(initial.ownership) : ANY);

  const cleanTextValue = (textValue) => (textValue.value !== '' ? textValue.value : null);

  const cleanArrayValue = (arrayValue) => (arrayValue.value.length > 0 ? arrayValue.value.map((item) => item.id) : null);

  // Emptied, the list selection is null
  const cleanSingleValue = (singleValue) => (singleValue.value ? singleValue.value.id : null);

  // Search parameters of the criteria, without the blank ones
  const params = computed(() => {
    let formData = {
      query: cleanTextValue(query),
      isbns: cleanTextValue(isbn),
      title: cleanTextValue(title),
      description: cleanTextValue(description),
      imprint_ids: cleanArrayValue(include_imprints),
      excluded_imprint_ids: cleanArrayValue(exclude_imprints),
      author_ids: cleanArrayValue(include_contributors),
      excluded_author_ids: cleanArrayValue(exclude_contributors),
      serie_ids: cleanArrayValue(include_series),
      excluded_serie_ids: cleanArrayValue(exclude_series),
      award_ids: cleanArrayValue(include_awards),
      excluded_award_ids: cleanArrayValue(exclude_awards),
      collection_ids: cleanArrayValue(include_collections),
      excluded_collection_ids: cleanArrayValue(exclude_collections),
      languages: cleanArrayValue(include_languages),
      excluded_languages: cleanArrayValue(exclude_languages),
      audiences: cleanArrayValue(include_audiences),
      excluded_audiences: cleanArrayValue(exclude_audiences),
      [props.classification_scheme]: cleanArrayValue(include_categories),
      [`excluded_${props.classification_scheme}`]: cleanArrayValue(exclude_categories),
      publication_date_start: cleanTextValue(publication_date_start),
      publication_date_end: cleanTextValue(publication_date_end),
      release_date: cleanTextValue(release_date),
      offer_creation_date_start: cleanTextValue(creation_date_start),
      offer_creation_date_end: cleanTextValue(creation_date_end),
      offer_create_date: cleanTextValue(offer_create_date),
      illustrated_books: cleanArrayValue(include_illustrated_books),
      excluded_illustrated_books: cleanArrayValue(exclude_illustrated_books),
      list_id: cleanSingleValue(list),
      subsets: catalogs.value,
      book_formats: [],
      epub_options: format_epub.value ? epub_options.value : null,
      audiobook_options: format_audiobook.value ? audiobook_options.value : null,
      accessibilities: accessibility.value === 'accessible' && accessibility_options.value.length > 0 ? accessibility_options.value : null,
      purchased: props.purchase_history && purchased.value !== ANY ? purchased.value : null,
      days_remaining: remaining_disabled.value ? null : cleanTextValue(days_remaining),
      loans_remaining: remaining_disabled.value ? null : cleanTextValue(loans_remaining),
      ownership: props.ownership_model && ownership.value !== ANY ? ownership.value : null,
      lifetime: lifetime.value !== ANY ? lifetime.value : null,
      tokens: tokens.value !== ANY ? tokens.value : null,
      concurrency: concurrency.value !== ANY ? concurrency.value : null,
      award_position: award_position.value !== ANY ? award_position.value : null,
      from_quebec: props.from_quebec_filter && from_quebec.value !== ANY ? from_quebec.value : null
    };
    let result =  Object.entries(formData).reduce((a, [k, v]) => (v == null ? a : (a[k] = v, a)), {});
    // Without languages, the search spans all of them. Within another form, the library's ones apply instead
    if (result.languages == undefined && !props.input_name) {
      result.languages = 'all';
    }
    if (format_epub.value) {
      result.book_formats.push('epub');
    }
    if (format_pdf.value) {
      result.book_formats.push('pdf');
    }
    if (format_audiobook.value) {
      result.book_formats.push('audiobook');
    }
    return result;
  });

  const toggle_epub = (value) => {
    if (value) {
      epub_options.value = [...DEFAULTS.epub_options];
    } else {
      epub_options.value = [];
    }
  };

  const toggle_audiobook = (value) => {
    if (value) {
      audiobook_options.value = [...DEFAULTS.audiobook_options];
    } else {
      audiobook_options.value = [];
    }
  };

  const accessibility_options_disabled = computed(() => {
    return accessibility.value !== 'accessible';
  });

  const shown = (key) => !props.shown_criteria || props.shown_criteria.includes(key);
  const ownership_shown = computed(() => props.ownership_model && shown('ownership'));
  const purchased_shown = computed(() => props.purchase_history && shown('purchased'));
  const date_shown = computed(() => shown('publication_date') || shown('creation_date'));
  const license_terms_shown = computed(() => ['lifetime', 'tokens', 'concurrency', 'award_position'].some(shown) || (props.from_quebec_filter && shown('from_quebec')));

  // Criteria cards stacked in columns, a third one for the purchase history
  const criteria_column_class = computed(() => {
    return [purchased_shown.value ? 'col-4' : 'col-6', 'd-flex', 'flex-column', 'gap-3'];
  });

  // The remaining days and loans only narrow the publications available in inventory
  const remaining_disabled = computed(() => {
    return !props.purchase_history || purchased.value !== 'available_in_inventory';
  });

  // Whether each criteria differs from its default value, and how to get it back to it.
  // A criteria is a list of [field, default value] pairs; the fields to reset only are cleared along without
  // making it active (the remaining days and loans, or the accessibility options, without their main choice)
  const differs = (value, default_value) => {
    if (!Array.isArray(default_value)) return (value || '') !== default_value;

    return value.length !== default_value.length || default_value.some((item) => !value.includes(item));
  };
  const criterion = (fields, reset_only = []) => ({
    active: computed(() => fields.some(([field, default_value]) => differs(field.value, default_value))),
    clear: () => [...fields, ...reset_only].forEach(([field, default_value]) => {
      field.value = Array.isArray(default_value) ? [...default_value] : default_value;
    })
  });
  const criteria = reactive({
    query: criterion([[query, '']]),
    isbn: criterion([[isbn, '']]),
    title: criterion([[title, '']]),
    description: criterion([[description, '']]),
    list: criterion([[list, '']]),
    publication_date: criterion([[release_date, ''], [publication_date_start, ''], [publication_date_end, '']]),
    creation_date: criterion([[offer_create_date, ''], [creation_date_start, ''], [creation_date_end, '']]),
    format: criterion([[format_pdf, true], [epub_options, DEFAULTS.epub_options], [audiobook_options, DEFAULTS.audiobook_options]]),
    catalog: criterion([[catalogs, DEFAULTS.catalogs]]),
    accessibility: criterion([[accessibility, ANY]], [[accessibility_options, DEFAULTS.accessibility_options]]),
    purchased: criterion([[purchased, ANY]], [[days_remaining, ''], [loans_remaining, '']]),
    ownership: criterion([[ownership, ANY]]),
    lifetime: criterion([[lifetime, ANY]]),
    tokens: criterion([[tokens, ANY]]),
    concurrency: criterion([[concurrency, ANY]]),
    award_position: criterion([[award_position, ANY]]),
    from_quebec: criterion([[from_quebec, ANY]])
  });

  // At least one accessibility option stays checked, none would filter on nothing while the card says otherwise
  const accessibility_option_disabled = (option) => {
    return accessibility_options_disabled.value || (accessibility_options.value.length === 1 && accessibility_options.value[0] === option);
  };

  watch(epub_options, (newValue) => {
    format_epub.value = newValue.length > 0;
  });

  watch(audiobook_options, (newValue) => {
    format_audiobook.value = newValue.length > 0;
  });

  // Whether a criteria narrows the titles, a blank form searching the whole catalog. The catalog type does not: it
  // differs from its default when both are checked, which widens the search
  const narrowed = computed(() => {
    const include_exclude = [
      include_imprints, exclude_imprints, include_contributors, exclude_contributors, include_series, exclude_series,
      include_awards, exclude_awards, include_collections, exclude_collections, include_languages, exclude_languages,
      include_audiences, exclude_audiences, include_categories, exclude_categories, include_illustrated_books, exclude_illustrated_books
    ];
    const active = Object.entries(criteria).some(([key, item]) => key !== 'catalog' && item.active);
    return active || include_exclude.some((items) => items.value.length > 0);
  });

  // Posted as search params along the ones the form opened with, so that only the changed criteria replace them
  const toQuery = (search_params) => new URLSearchParams(search_params).toString();
  const initial_query = ref('');
  const edited_query = computed(() => toQuery(params.value));
  onMounted(() => {
    initial_query.value = props.initial_criteria || edited_query.value;
  });

  // Within another form, the fields are named under the search params, so that they are not posted as params of their
  // own. Alone, they keep their name, or the generated one of a group (null)
  const field_name = (name, standalone_name = name) => {
    if (props.input_name) return `${props.input_name}[fields][${name}]`;

    return standalone_name ?? undefined;
  };

  // Within another form, Enter in a field would submit it
  const preventSubmit = (event) => {
    if (props.input_name && event.target.tagName === 'INPUT') event.preventDefault();
  };

  defineExpose({
    // The search params of the criteria, as a query string
    query: edited_query,
    narrowed
  });
</script>

<template>
  <div @keydown.enter="preventSubmit">
    <div class="d-flex flex-column gap-3">
      <CriteriaCard v-if="shown('query')" id="query" :title="t('advanced_search.query.label')" label_for="query" :active="criteria.query.active" @clear="criteria.query.clear">
        <BFormInput v-model="query" id="query" />
      </CriteriaCard>
      <CriteriaCard v-if="shown('isbn')" id="isbn" :title="t('advanced_search.isbns.label')" label_for="isbn" :active="criteria.isbn.active" @clear="criteria.isbn.clear">
        <BFormTextarea v-model="isbn" rows="3" id="isbn" />
        <div class="form-text">{{ t('advanced_search.isbns.description') }}</div>
      </CriteriaCard>
      <CriteriaCard v-if="shown('title')" id="title" :title="t('advanced_search.title.label')" label_for="title" :active="criteria.title.active" @clear="criteria.title.clear">
        <BFormInput v-model="title" id="title" />
      </CriteriaCard>
      <CriteriaCard v-if="shown('description')" id="description" :title="t('advanced_search.description.label')" label_for="description" :active="criteria.description.active" @clear="criteria.description.clear">
        <BFormInput v-model="description" id="description" />
      </CriteriaCard>
      <IncludeExclude v-if="shown('imprint')" card object_type="imprint" v-model:inclusions="include_imprints" v-model:exclusions="exclude_imprints" />
      <IncludeExclude v-if="shown('contributor')" card object_type="contributor" v-model:inclusions="include_contributors" v-model:exclusions="exclude_contributors" />
      <IncludeExclude v-if="shown('serie')" card object_type="serie" v-model:inclusions="include_series" v-model:exclusions="exclude_series" />
      <IncludeExclude v-if="shown('award')" card object_type="award" v-model:inclusions="include_awards" v-model:exclusions="exclude_awards" />
      <IncludeExclude v-if="shown('collection')" card object_type="collection" v-model:inclusions="include_collections" v-model:exclusions="exclude_collections" />
      <IncludeExclude v-if="shown('language')" card object_type="language" v-model:inclusions="include_languages" v-model:exclusions="exclude_languages" :options="props.languages" />
      <IncludeExclude v-if="shown('audience')" card object_type="audience" v-model:inclusions="include_audiences" v-model:exclusions="exclude_audiences" :options="props.audiences" />
      <IncludeExclude v-if="shown('classification')" card object_type="classification" :scheme="props.classification_scheme" v-model:inclusions="include_categories" v-model:exclusions="exclude_categories" />
      <IncludeExclude v-if="shown('illustrated_books')" card object_type="illustrated_books" v-model:inclusions="include_illustrated_books" v-model:exclusions="exclude_illustrated_books" :options="props.illustrated_books" />
      <CriteriaCard v-if="shown('list')" id="list" :title="t('advanced_search.list.label')" label_for="list_include" :active="criteria.list.active" @clear="criteria.list.clear">
        <SuggestionBox v-model="list" object_type="list" :inclusion="'include'" :options="props.lists" :multiple="false" />
      </CriteriaCard>
    </div>

    <div class="row g-3 mt-0" v-if="date_shown">
      <div class="col-6" v-if="shown('publication_date')">
        <CriteriaCard id="publication_date" :title="t('advanced_search.publication_date.label')" class="h-100" body_class="row" :active="criteria.publication_date.active" @clear="criteria.publication_date.clear">
          <div class="col-4">
            <label for="release_date" class="form-label">{{ t('advanced_search.range') }}</label>
            <BFormSelect v-model="release_date" :options="date_range_options" id="release_date" />
          </div>
          <div class="col-4">
            <label for="publication_date_start" class="form-label">{{ t('advanced_search.start_date') }}</label>
            <BFormInput id="publication_date_start" type="date" v-model="publication_date_start" />
          </div>
          <div class="col-4">
            <label for="publication_date_end" class="form-label">{{ t('advanced_search.end_date') }}</label>
            <BFormInput id="publication_date_end" type="date" v-model="publication_date_end" />
          </div>
        </CriteriaCard>
      </div>
      <div class="col-6" v-if="shown('creation_date')">
        <CriteriaCard id="creation_date" :title="t('advanced_search.creation_date.label')" class="h-100" body_class="row" :active="criteria.creation_date.active" @clear="criteria.creation_date.clear">
          <div class="col-4">
            <label for="offer_create_date" class="form-label">{{ t('advanced_search.range') }}</label>
            <BFormSelect v-model="offer_create_date" :options="date_range_options" id="offer_create_date" />
          </div>
          <div class="col-4">
            <label for="creation_date_start" class="form-label">{{ t('advanced_search.start_date') }}</label>
            <BFormInput id="creation_date_start" type="date" v-model="creation_date_start" />
          </div>
          <div class="col-4">
            <label for="creation_date_end" class="form-label">{{ t('advanced_search.end_date') }}</label>
            <BFormInput id="creation_date_end" type="date" v-model="creation_date_end" />
          </div>
        </CriteriaCard>
      </div>
    </div>

    <div class="row g-3 mt-0">
      <div :class="criteria_column_class">
        <CriteriaCard v-if="shown('format')" :class="{ 'flex-grow-1': !shown('catalog') }" id="format" :title="t('advanced_search.format.label')" :active="criteria.format.active" @clear="criteria.format.clear">
          <BFormCheckbox v-model="format_epub" @update:model-value="toggle_epub">{{ t('advanced_search.format.epub') }}</BFormCheckbox>
          <BFormCheckboxGroup v-model="epub_options" :name="field_name('epub_options', null)" stacked class="ms-3">
            <BFormCheckbox value="reflowable">{{ t('advanced_search.format.reflowable') }}</BFormCheckbox>
            <BFormCheckbox value="fixed_layout">{{ t('advanced_search.format.fixed_layout') }}</BFormCheckbox>
          </BFormCheckboxGroup>
          <BFormCheckbox v-model="format_pdf">{{ t('advanced_search.format.pdf') }}</BFormCheckbox>
          <BFormCheckbox v-model="format_audiobook" @update:model-value="toggle_audiobook">{{ t('advanced_search.format.audiobook') }}</BFormCheckbox>
          <BFormCheckboxGroup v-model="audiobook_options" :name="field_name('audiobook_options', null)" stacked class="ms-3">
            <BFormCheckbox value="unabridged">{{ t('advanced_search.format.unabridged') }}</BFormCheckbox>
            <BFormCheckbox value="abridged">{{ t('advanced_search.format.abridged') }}</BFormCheckbox>
          </BFormCheckboxGroup>
        </CriteriaCard>
        <CriteriaCard v-if="shown('catalog')" id="catalog" :title="t('advanced_search.catalog.label')" class="flex-grow-1" :active="criteria.catalog.active" @clear="criteria.catalog.clear">
          <BFormCheckboxGroup v-model="catalogs" :name="field_name('catalogs', null)" stacked>
            <BFormCheckbox value="traditional">{{ t('advanced_search.catalog.traditional') }}</BFormCheckbox>
            <BFormCheckbox value="self_published">{{ t('advanced_search.catalog.self_published') }}</BFormCheckbox>
          </BFormCheckboxGroup>
        </CriteriaCard>
      </div>
      <div :class="criteria_column_class">
        <CriteriaCard v-if="shown('accessibility')" :class="{ 'flex-grow-1': !ownership_shown }" id="accessibility" :title="t('advanced_search.accessibility.label')" :active="criteria.accessibility.active" @clear="criteria.accessibility.clear">
          <BFormRadio v-model="accessibility" :name="field_name('accessibility')" :value="ANY">{{ t('advanced_search.accessibility.all') }}</BFormRadio>
          <BFormRadio v-model="accessibility" :name="field_name('accessibility')" value="accessible">{{ t('advanced_search.accessibility.accessible') }}</BFormRadio>
          <BFormCheckboxGroup v-model="accessibility_options" :name="field_name('accessibility_options', null)" stacked class="ms-3">
            <BFormCheckbox v-for="option in props.accessibilities" :key="option" :value="option" :disabled="accessibility_option_disabled(option)">{{ t(`advanced_search.accessibility.${option}`) }}</BFormCheckbox>
          </BFormCheckboxGroup>
          <div class="form-text ms-3">{{ t('advanced_search.accessibility.every_option') }}</div>
        </CriteriaCard>
        <CriteriaCard v-if="ownership_shown" id="ownership" :title="t('advanced_search.ownership.label')" class="flex-grow-1" :active="criteria.ownership.active" @clear="criteria.ownership.clear">
          <BFormRadioGroup v-model="ownership" :name="field_name('ownership')" stacked>
            <BFormRadio :value="ANY">{{ t('advanced_search.ownership.all') }}</BFormRadio>
            <BFormRadio value="true">{{ t('advanced_search.ownership.true') }}</BFormRadio>
            <BFormRadio value="false">{{ t('advanced_search.ownership.false') }}</BFormRadio>
          </BFormRadioGroup>
        </CriteriaCard>
      </div>
      <div :class="criteria_column_class" v-if="purchased_shown">
        <CriteriaCard id="purchased" :title="t('advanced_search.purchased.label')" class="flex-grow-1" :active="criteria.purchased.active" @clear="criteria.purchased.clear">
          <BFormRadio v-model="purchased" :name="field_name('purchased')" :value="ANY">{{ t('advanced_search.purchased.all') }}</BFormRadio>
          <template v-for="status in props.purchased_statuses.options" :key="status">
            <BFormRadio v-model="purchased" :name="field_name('purchased')" :value="status">{{ t(`advanced_search.purchased.${status}`) }}</BFormRadio>
            <div v-if="status === 'purchased'" class="ms-3">
              <template v-for="sub_status in props.purchased_statuses.sub_options" :key="sub_status">
                <BFormRadio v-model="purchased" :name="field_name('purchased')" :value="sub_status">{{ t(`advanced_search.purchased.${sub_status}`) }}</BFormRadio>
                <div v-if="sub_status === 'available_in_inventory'" class="ms-4">
                  <div class="form-text">{{ t('advanced_search.purchased.less_than') }}</div>
                  <div class="row g-2 align-items-center mt-0">
                    <div class="col-4">
                      <BFormInput v-model="days_remaining" id="days_remaining" type="number" min="1" placeholder="30" size="sm" :disabled="remaining_disabled" />
                    </div>
                    <label for="days_remaining" class="col-auto">{{ t('advanced_search.purchased.days_remaining') }}</label>
                  </div>
                  <div class="row g-2 align-items-center mt-1">
                    <div class="col-4">
                      <BFormInput v-model="loans_remaining" id="loans_remaining" type="number" min="1" placeholder="10" size="sm" :disabled="remaining_disabled" />
                    </div>
                    <label for="loans_remaining" class="col-auto">{{ t('advanced_search.purchased.loans_remaining') }}</label>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </CriteriaCard>
      </div>
    </div>

    <div class="row g-3 mt-0" v-if="license_terms_shown">
      <div class="col-4" v-if="shown('lifetime')">
        <CriteriaCard id="lifetime" :title="t('advanced_search.lifetime.label')" label_for="lifetime" class="h-100" :active="criteria.lifetime.active" @clear="criteria.lifetime.clear">
          <BFormSelect v-model="lifetime" :options="license_term_options.lifetime" id="lifetime" />
        </CriteriaCard>
      </div>
      <div class="col-4" v-if="shown('tokens')">
        <CriteriaCard id="tokens" :title="t('advanced_search.tokens.label')" label_for="tokens" class="h-100" :active="criteria.tokens.active" @clear="criteria.tokens.clear">
          <BFormSelect v-model="tokens" :options="license_term_options.tokens" id="tokens" />
        </CriteriaCard>
      </div>
      <div class="col-4" v-if="shown('concurrency')">
        <CriteriaCard id="concurrency" :title="t('advanced_search.concurrency.label')" label_for="concurrency" class="h-100" :active="criteria.concurrency.active" @clear="criteria.concurrency.clear">
          <BFormSelect v-model="concurrency" :options="license_term_options.concurrency" id="concurrency" />
        </CriteriaCard>
      </div>
      <div class="col-4" v-if="shown('award_position')">
        <CriteriaCard id="award_position" :title="t('advanced_search.award_position.label')" label_for="award_position" class="h-100" :active="criteria.award_position.active" @clear="criteria.award_position.clear">
          <BFormSelect v-model="award_position" :options="award_position_options" id="award_position" />
        </CriteriaCard>
      </div>
      <div class="col-4" v-if="props.from_quebec_filter && shown('from_quebec')">
        <CriteriaCard id="from_quebec" :title="t('advanced_search.from_quebec.label')" label_for="from_quebec" class="h-100" :active="criteria.from_quebec.active" @clear="criteria.from_quebec.clear">
          <BFormSelect v-model="from_quebec" :options="from_quebec_options" id="from_quebec" />
        </CriteriaCard>
      </div>
    </div>

    <template v-if="props.input_name && initial_query">
      <input type="hidden" :name="`${props.input_name}[initial]`" :value="initial_query" />
      <input type="hidden" :name="`${props.input_name}[edited]`" :value="edited_query" />
    </template>
  </div>
</template>

<style scoped lang="scss">
  input[type="date"]:before {
    display: none !important;
  }

  input[type="date"]:focus:before {
    display: none !important;
  }
</style>

<i18n src="../../locales/en.json" global></i18n>
<i18n src="../../locales/fr.json" global></i18n>
<i18n src="../../locales/es.json" global></i18n>
<i18n src="../../locales/it.json" global></i18n>
<i18n src="../../locales/pt.json" global></i18n>
<i18n src="../../locales/de.json" global></i18n>
